/**
 * Owns a parcel plan for the flows that build one from scratch.
 *
 * Two properties matter, and between them they remove four coordinating workarounds that previously
 * existed in the combine flow:
 *
 *   1. `setDimensions` reflows per-parcel weight against the live plan immediately. Weight is
 *      therefore always correct AND manual rearrangement is never discarded, so there is no need
 *      either for a deliberately-incomplete useMemo dep array (which existed to stop a dimensions
 *      edit wiping the arrangement) or for a compensating re-split at save time.
 *   2. Reseeding is EXPLICIT - it happens on setParcelCount or reseed(), never as a side effect of a
 *      prop's object identity changing. That removes the remount key and the fallback-to-seed
 *      validation, and it removes a render feedback loop: the editor used to reseed from its own
 *      reported-up value, terminating only because the same array reference happened to be reused.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { ConsignmentApiModel, Dimensions } from '~/lib/api/smartsend/types';
import { SplitConsignmentWeight } from '../../components/smartsend/panels/consignments/GetConsignmentFromItem';
import { ParcelEditActions, bindParcelEdits } from './planEdits';

export interface UseParcelPlanArgs {
  /** Builds a fresh plan. Called on mount, on setParcelCount and on reseed - never implicitly. */
  seed: (parcelCount: number, dimensions: Dimensions) => ConsignmentApiModel;
  initialParcelCount: number;
  initialDimensions: Dimensions;
  /**
   * How a dimensions change divides the weight across the parcels. Defaults to an even split, which is
   * right for a consignment - one shipment in several boxes, where only the sum has to match.
   *
   * Flows where each box becomes its OWN shipment want per-unit instead (applyPerUnitWeight), or a box of
   * five items and a box of one declare the same weight. Supplying it here rather than only at seed time
   * matters: without it, editing the shipment-wide weight would silently revert to the even split.
   */
  divideWeight?: (plan: ConsignmentApiModel, dimensions: Dimensions) => ConsignmentApiModel;
}

export interface UseParcelPlan {
  plan: ConsignmentApiModel;
  /** What the user ASKED for. May exceed plan.Parcels.length when the allocator clamps. */
  parcelCount: number;
  dimensions: Dimensions;
  setParcelCount: (count: number) => void;
  setDimensions: (dimensions: Dimensions) => void;
  reseed: () => void;
  /** Replace the plan wholesale, for a caller that already has one built. */
  setPlan: (plan: ConsignmentApiModel) => void;
  edit: ParcelEditActions;
}

/**
 * Pushes a dimensions change onto every parcel and re-divides the weight. Contents are untouched.
 * Mirrors the single-item path in ItemDetails, which is the behaviour we want everywhere.
 */
const evenlyDividedWeight = (plan: ConsignmentApiModel, dimensions: Dimensions): ConsignmentApiModel => {
  SplitConsignmentWeight(dimensions, plan);
  return plan;
};

const applyDimensions = (
  plan: ConsignmentApiModel,
  dimensions: Dimensions,
  divideWeight: (plan: ConsignmentApiModel, dimensions: Dimensions) => ConsignmentApiModel
): ConsignmentApiModel => {
  const next: ConsignmentApiModel = {
    // LocalUnits is dropped deliberately rather than carried by the spread: these dimensions did not
    // come from typing into this parcel, so a retained as-entered original would display a number
    // contradicting the value now stored. Display falls back to converting, which is correct here.
    Parcels: plan.Parcels.map((parcel) => ({ ...parcel, Dimensions: { ...dimensions }, LocalUnits: undefined }))
  };
  return divideWeight(next, dimensions);
};

export function useParcelPlan(args: UseParcelPlanArgs): UseParcelPlan {
  // Held in refs so the callbacks below are stable and never need the seed in a dep array - callers
  // pass an inline arrow, which would otherwise change identity on every render.
  const seedRef = useRef(args.seed);
  seedRef.current = args.seed;
  const divideWeightRef = useRef(args.divideWeight ?? evenlyDividedWeight);
  divideWeightRef.current = args.divideWeight ?? evenlyDividedWeight;

  const [dimensions, setDimensionsState] = useState<Dimensions>(args.initialDimensions);
  const [parcelCount, setParcelCountState] = useState(args.initialParcelCount);
  const [plan, setPlanState] = useState<ConsignmentApiModel>(() =>
    args.seed(args.initialParcelCount, args.initialDimensions)
  );

  const planRef = useRef(plan);
  planRef.current = plan;
  const dimensionsRef = useRef(dimensions);
  dimensionsRef.current = dimensions;
  const countRef = useRef(parcelCount);
  countRef.current = parcelCount;

  const setPlan = useCallback((next: ConsignmentApiModel) => {
    planRef.current = next;
    setPlanState(next);
  }, []);

  const setParcelCount = useCallback(
    (count: number) => {
      setParcelCountState(count);
      setPlan(seedRef.current(count, dimensionsRef.current));
    },
    [setPlan]
  );

  const setDimensions = useCallback(
    (next: Dimensions) => {
      setDimensionsState(next);
      setPlan(applyDimensions(planRef.current, next, divideWeightRef.current));
    },
    [setPlan]
  );

  const reseed = useCallback(() => {
    setPlan(seedRef.current(countRef.current, dimensionsRef.current));
  }, [setPlan]);

  const edit = useMemo(
    () =>
      bindParcelEdits(
        () => planRef.current,
        setPlan,
        () => dimensionsRef.current
      ),
    [setPlan]
  );

  return { plan, parcelCount, dimensions, setParcelCount, setDimensions, reseed, setPlan, edit };
}
